import { useEffect, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useIsPro } from "@/hooks/useIsPro";
import { useAppSetting } from "@/hooks/useAppSetting";
import { APP_SETTING_KEYS } from "@/hooks/appSettingKeys";
import { usePreferences, type UserPrefs } from "@/hooks/useUserPreferences";
import { trackFeature } from "@/lib/analytics";
import { showSaveFailedToast } from "@/lib/saveFailureToast";
import {
  isNotificationsAvailable,
  requestPushPermission,
  getPushPermissionStatus,
  openIOSNotificationSettings,
  markSoftPrompted,
  isAlreadySubscribed,
  identifyOneSignalUser,
} from "@/lib/notifications";
import NotificationSoftPrompt from "./NotificationSoftPrompt";
import SmsSetupSheet from "./SmsSetupSheet";
import { ProUpsellLink } from "@/components/ProUpsell";
import SaveKinContactBlock, { KIN_NUMBER_DISPLAY, KIN_NUMBER_E164 } from "./SaveKinContactBlock";
import {
  buildPostVerifyPatch,
  resolveTimezone,
  DELIVERY_FEATURES as FEATURES,
  PUSH_FIELD,
  TEXT_FIELD,
  type DeliveryFeature as FeatureKey,
} from "@/lib/deliveryChannel";
import { reportError } from "@/lib/reportError";

/**
 * "How Kin reaches you" — the single delivery screen.
 *
 * Replaces BOTH the old NotificationsSection ("How Kin reaches you") and
 * KinTextsSection ("Kin texts you"), which were two cards describing one
 * decision. The split forced her to answer "where should this land?" twice, in
 * two different vocabularies, and produced the contradiction #431 chased down:
 * a per-row text toggle sitting next to a chooser that derived its label from
 * that field AND the push one, so "Morning briefing OFF" could appear directly
 * above a chooser reading "Push".
 *
 * The shape now is WHAT then HOW:
 *   WHAT KIN SENDS  — one row per thing, each an on/off for the thing itself,
 *                     with its schedule inline where it has one.
 *   HOW THEY ARRIVE — one Push/Text/Both chooser governing every row that's on.
 *
 * Rows come from DELIVERY_FEATURES, so adding one is a matter of adding its
 * column pair there plus a label and a subtext below — every behavior on this
 * screen (the chooser's derived position, the off-clears-both rule, the
 * post-verify patch) already iterates that list rather than naming rows.
 *
 * ── The field model (read this before changing a write) ─────────────────────
 *
 * There is deliberately NO global "delivery channel" column. The chooser is one
 * control that writes a push field and a text field for each row:
 *
 *              push                                  text
 *   morning    notifications_morning_enabled         sms_morning_enabled
 *   weekly     notifications_weekly_enabled          sms_weekly_enabled
 *   tasks      notifications_task_updates_enabled    sms_task_updates_enabled
 *   ready      notifications_ready_enabled           sms_ready_enabled
 *
 * sms_weekly_enabled and notifications_task_updates_enabled arrived with
 * supabase/migrations/20260802060000_notification_delivery_prefs.sql; the
 * `ready` pair with 20260913000000_user_preferences_ready_alerts.sql (#109),
 * which also backfills it from the `tasks` pair — Kin Inbox and Beauty
 * Analyzer delivered under Task updates until then.
 *
 * A row is ON when EITHER of its two fields is true. That derivation is the
 * whole design: "is Kin sending this?" and "how does it arrive?" become
 * orthogonal, so the row toggle and the chooser can never disagree the way they
 * used to. It is also what makes the morning "Send at" picker correct on every
 * channel — the gate #431 shipped (push OR text) generalized to all three rows.
 *
 * Turning a row OFF clears both of its fields. Turning it ON applies whatever
 * the chooser currently says. Moving the chooser rewrites both fields on every
 * row that's already on, and leaves rows that are off alone.
 *
 * ── Opt-out is read-only, on purpose ────────────────────────────────────────
 *
 * When she replies STOP, the carrier — not us — stops her texts, and only an
 * inbound START from her own handset undoes it. This component therefore never
 * writes sms_opted_out: false. The old KinTextsSection did (any toggle-on
 * cleared it), which let the app claim it had turned texts back on when it had
 * not. State (c) below explains the situation and hands her the number; that is
 * the entire permitted surface. sms_opted_out flips back to false server-side
 * via the START webhook, and this screen returns to state (b) on its own.
 */

type Channel = "push" | "text" | "both";

// FEATURES / PUSH_FIELD / TEXT_FIELD now live in @/lib/deliveryChannel — three
// surfaces finish SMS verification and they must agree on the mapping (#470).

const ROW_LABEL: Record<FeatureKey, string> = {
  morning: "Morning Note",
  weekly: "Weekly planning reminder",
  tasks: "Task updates",
  ready: "Ready for you",
};

const ROW_SUBTEXT: Record<FeatureKey, string> = {
  morning: "Your day, sent to you every morning ☀️",
  weekly: "A Sunday nudge to plan your week 💜",
  tasks: "Meal plans, restocks, reminders",
  // #109 — the row's copy has to say "something you asked for", because that
  // is the whole reason it is not part of Task updates above.
  ready: "When something you asked Kin for is done",
};

const DAY_INITIALS = ["S", "M", "T", "W", "T", "F", "S"];
const DAY_NAMES = [
  "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",
];

/** Formats a stored E.164 US number ("+15551234567") as "(555) 123-4567". Falls back to the raw value for any other shape. */
function formatUsNumber(e164: string): string {
  const match = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164);
  if (!match) return e164;
  return `(${match[1]}) ${match[2]}-${match[3]}`;
}

const HowKinReachesYou = () => {
  const navigate = useNavigate();
  const { user, isAdmin, adminPreview } = useAuth();
  // ⚠️ #994 — admin god-mode requires BOTH flags, matching the other twelve
  // consumers. `adminPreview` alone would hand out Pro to any row that had
  // `entitlements.admin_preview` set without being an admin — a mistyped
  // user_id or a copied test row. That is the worst shape for a gate bug: it
  // holds everywhere you would think to check and leaks in the two places
  // nobody would.
  const isPro = useIsPro() || (isAdmin && adminPreview);
  const smsLive = useAppSetting(APP_SETTING_KEYS.SMS_LIVE);
  const { prefs, loading: prefsLoading, updatePreferences, refetchPreferences } = usePreferences();

  const [pushAvailable] = useState(() => isNotificationsAvailable());
  const [permission, setPermission] = useState<"granted" | "denied" | "default">("default");
  const [sheetOpen, setSheetOpen] = useState(false);
  const [contactHelperOpen, setContactHelperOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [softPromptOpen, setSoftPromptOpen] = useState(false);
  // What to finish once iOS permission comes back — either a row we were turning
  // on, or a chooser selection that includes Push.
  const [pending, setPending] = useState<
    { kind: "row"; feature: FeatureKey } | { kind: "channel"; channel: Channel } | null
  >(null);

  // Re-read iOS push permission on mount, focus, and visibility change. A user
  // who flips permission in iOS Settings and comes back should see this screen
  // reflect reality rather than her last in-app tap.
  useEffect(() => {
    if (!pushAvailable) return;
    let cancelled = false;
    const refresh = async () => {
      const status = await getPushPermissionStatus();
      if (!cancelled) setPermission(status);
    };
    refresh();
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", refresh);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", refresh);
    };
  }, [pushAvailable]);

  // Nothing to show if neither channel exists on this build: no native push
  // bridge AND texts switched off globally. Note this is deliberately looser
  // than the old NotificationsSection gate (`isNotificationsAvailable()` alone),
  // which hid the entire screen — including the text controls and the send-time
  // picker — from a Pro text-only mom on the web app. #431 flagged that; the
  // merge is what makes it fixable, since the text half no longer lives behind
  // the push half's gate.
  if (!pushAvailable && !smsLive) return null;

  // Push is a device capability, not a preference. Without the native bridge
  // there is no push to choose, so the chooser stands down and everything this
  // screen writes goes to the text fields — see the note on textOnlyDevice below.
  const textOnlyDevice = !pushAvailable;

  const verified = !!prefs.phone_verified_at && !!prefs.phone_number;
  const optedOut = !!prefs.sms_opted_out;
  // T4 (#1141) — "Text back & forth with Kin".
  //
  // ⚠️ `!== false`, not `=== true`. The column is NOT NULL with default true,
  // but until the migration runs it is simply absent from the prefs row, and
  // absent must read as ON — the same rule kin-sms applies on the server. A
  // `=== true` here would render the toggle OFF for everyone in the window
  // between deploy and SQL, and one tap from her would then WRITE false and
  // genuinely switch her texting off.
  const twoWayOn = (prefs as { sms_two_way_enabled?: boolean }).sms_two_way_enabled !== false;
  // Texts can actually leave the building only for a verified, opted-in Pro on a
  // build where SMS is switched on at all.
  //
  // This predicate is LOAD-BEARING, not descriptive. Selecting Text writes
  // push=false across every enabled row; if a text then can't actually be sent,
  // she has silently switched off everything and gets nothing. send-sms rejects
  // her at Gate 2 as not_opted_in and says so to nobody. That is the same
  // silent-total-loss shape as the dispatcher bug fixed on Aug 2 — picking Text
  // and going quiet — so the guard belongs at the only place that can prevent
  // it: before the write.
  const textDeliverable = smsLive && isPro && verified && !optedOut;

  const rowOn = (f: FeatureKey) => !!prefs[PUSH_FIELD[f]] || !!prefs[TEXT_FIELD[f]];
  const anyRowOn = FEATURES.some(rowOn);

  // The chooser's position is DERIVED from the six fields rather than stored, so
  // it can never drift from what the dispatchers actually read. Null when nothing
  // is on at all — no pill lit, rather than a lie about "Push".
  const pushAny = FEATURES.some((f) => !!prefs[PUSH_FIELD[f]]);
  const textAny = FEATURES.some((f) => !!prefs[TEXT_FIELD[f]]);
  const current: Channel | null =
    pushAny && textAny ? "both" : pushAny ? "push" : textAny ? "text" : null;

  // Channel applied when she switches a row on and the chooser has no position
  // yet (everything was off). Prefer whichever channel she can actually receive.
  const fallbackChannel: Channel = textOnlyDevice ? "text" : "push";
  const effectiveChannel: Channel = current ?? fallbackChannel;

  // ── The one write path ────────────────────────────────────────────────────
  // #438's rule, applied to every control on this screen: optimistic so the tap
  // feels instant, and on a lost write put the control back AND say so once.
  // updatePreferences RESOLVES the PostgrestError rather than throwing (#416),
  // so this has to be checked, never caught.
  const persist = async (patch: Partial<UserPrefs>): Promise<boolean> => {
    const timezone = resolveTimezone();
    // #555 — resolveTimezone() is Intl.DateTimeFormat().resolvedOptions(),
    // i.e. the browser clock. She is deliberately configuring DELIVERY here,
    // not picking a timezone, so the provenance is still 'device'. It travels
    // with the value so the two can never drift apart.
    const full = {
      ...patch,
      ...(timezone
        ? { notifications_timezone: timezone, notifications_timezone_source: "device" as const }
        : {}),
    };
    // Snapshot exactly the fields this write touches. Rolling back the whole prefs
    // object would also undo anything she changed while the write was in flight.
    const previous = Object.fromEntries(
      Object.keys(full).map((k) => [k, prefs[k as keyof UserPrefs]])
    ) as Partial<UserPrefs>;
    const error = await updatePreferences(full);
    if (error) {
      reportError("[HowKinReachesYou] save failed", error, { tags: { area: "delivery-channel" } });
      await updatePreferences(previous, { write: false });
      showSaveFailedToast();
      return false;
    }
    return true;
  };

  const showDenialToast = () => {
    toast("You'll need to enable notifications in your iPhone Settings.", {
      action: { label: "Open Settings", onClick: () => openIOSNotificationSettings() },
    });
  };

  /**
   * Text is the channel she's reaching for but it can't deliver yet. Send her to
   * whatever would actually fix that, and write nothing.
   *
   * Note there is no !smsLive branch: the component returns null when there is
   * neither a push bridge nor SMS, so every caller that reaches here on a
   * text-only device is on a build where SMS is live.
   */
  const routeToTextFix = () => {
    if (!isPro) {
      trackFeature("delivery_channel_pro_locked", { channel: "text" });
      navigate("/pro");
      return;
    }
    if (optedOut) {
      // Only an inbound START lifts a carrier STOP — there is no flow to open.
      toast("Your texts are paused until you text START to " + KIN_NUMBER_DISPLAY + ".");
      return;
    }
    trackFeature("delivery_channel_needs_setup", { channel: "text" });
    setSheetOpen(true);
  };

  /** Write a row ON at the given channel. Side effects only run if the row landed. */
  const commitRowOn = async (f: FeatureKey, channel: Channel) => {
    let wantsPush = (channel === "push" || channel === "both") && pushAvailable;
    // textDeliverable, not isPro. The chooser can be sitting on Text from a time
    // when texts DID work — she picked Text while verified and has since replied
    // STOP. Switching a row on then would write text-only to a channel that
    // can't deliver, and she'd get nothing. The fallback below catches it and
    // gives her push instead, which is visible (the chooser moves to Push) rather
    // than silent.
    let wantsText = (channel === "text" || channel === "both") && textDeliverable;
    // A row that saved with both halves false would read as OFF the moment it
    // rendered — the tap would look like it did nothing. Fall back to the channel
    // she can actually receive rather than saving a row into limbo.
    if (!wantsPush && !wantsText) {
      if (pushAvailable) {
        wantsPush = true;
      } else if (textDeliverable) {
        wantsText = true;
      } else {
        // No push bridge on this device AND text can't be delivered, so there is
        // no channel to switch on at all. The old `else wantsText = true` wrote
        // an undeliverable text preference here — the row would read ON and
        // nothing would ever arrive, which is the exact silence this whole guard
        // exists to prevent, surviving in its own fallback. Refuse and route to
        // the fix instead.
        routeToTextFix();
        return;
      }
    }
    const ok = await persist({
      [PUSH_FIELD[f]]: wantsPush,
      [TEXT_FIELD[f]]: wantsText,
    } as Partial<UserPrefs>);
    if (!ok) return;
    trackFeature(`delivery_${f}_enabled`, { channel });
  };

  const handleTwoWayToggle = async (next: boolean) => {
    // Optimistic, then CHECKED (#416/#545): updatePreferences RESOLVES the
    // error rather than throwing, so an unchecked write leaves the switch
    // showing a state the database never took.
    const error = await updatePreferences({ sms_two_way_enabled: next } as Partial<UserPrefs>);
    if (error) {
      showSaveFailedToast();
      return;
    }
    trackFeature("sms_two_way_toggled", { on: next });
    toast.success(next ? "Texting back is on 💜" : "Texting back is off");
  };

  const handleRowToggle = async (f: FeatureKey, nextOn: boolean) => {
    if (!nextOn) {
      // OFF means off everywhere — both halves cleared, so the row can't come
      // back as "on" on the channel she didn't just switch off.
      const ok = await persist({
        [PUSH_FIELD[f]]: false,
        [TEXT_FIELD[f]]: false,
      } as Partial<UserPrefs>);
      if (!ok) return;
      trackFeature(`delivery_${f}_disabled`);
      return;
    }
    const channel = effectiveChannel;
    const needsPush = (channel === "push" || channel === "both") && pushAvailable;
    if (needsPush && permission !== "granted") {
      if (await isAlreadySubscribed()) {
        await commitRowOn(f, channel);
        return;
      }
      setPending({ kind: "row", feature: f });
      setSoftPromptOpen(true);
      return;
    }
    await commitRowOn(f, channel);
  };

  /** Move the chooser. Rewrites both halves of every row that's currently on. */
  const commitChannel = async (next: Channel) => {
    const wantsPush = next === "push" || next === "both";
    const wantsText = next === "text" || next === "both";
    const patch: Record<string, boolean> = {};
    for (const f of FEATURES) {
      if (!rowOn(f)) continue; // rows that are off stay off on both channels
      patch[PUSH_FIELD[f] as string] = wantsPush;
      patch[TEXT_FIELD[f] as string] = wantsText;
    }
    if (Object.keys(patch).length === 0) return;
    const ok = await persist(patch as Partial<UserPrefs>);
    if (!ok) return;
    trackFeature("delivery_channel_set", { channel: next });
  };

  const handleChannelSelect = async (next: Channel) => {
    // Free tier: Text and Both are the Pro surface. Tap routes to the real
    // subscribe flow rather than writing anything.
    if (!isPro && next !== "push") {
      trackFeature("delivery_channel_pro_locked", { channel: next });
      navigate("/pro");
      return;
    }
    // Pro, but texts can't reach her yet. Send her to the thing that would fix
    // that instead of writing a preference that silences her. Deliberately NOT
    // a disabled pill: "Text" is a real option for her, it just needs a number
    // first, and a dead control wouldn't say which.
    if (next !== "push" && !textDeliverable) {
      routeToTextFix();
      return;
    }
    if (next === current) return;
    const wantsPush = next === "push" || next === "both";
    if (wantsPush && pushAvailable && permission !== "granted") {
      if (await isAlreadySubscribed()) {
        await commitChannel(next);
        return;
      }
      setPending({ kind: "channel", channel: next });
      setSoftPromptOpen(true);
      return;
    }
    await commitChannel(next);
  };

  const handleTimeChange = async (next: string) => {
    await persist({ notifications_morning_time: next });
  };

  const handleWeeklyDayChange = async (next: number) => {
    await persist({ notifications_weekly_day_of_week: next });
  };

  const handleWeeklyTimeChange = async (next: string) => {
    await persist({ notifications_weekly_time: next });
  };

  const handleVerified = async () => {
    trackFeature("sms_verified");
    // The Edge Function already wrote phone_number/phone_verified_at/sms_opted_out
    // server-side (service-role write) — refetch to pick that server truth up
    // rather than racing a second client-side write of the same fields.
    //
    // Shared with the Morning Notes chip and ProSetupFlowModal (#470) so all
    // three surfaces answer "she just verified a number" identically: add text
    // to the rows already on, never resurrect a row she switched off, never
    // touch push.
    //
    // Note this no longer forces push on for rows that were text-only. The old
    // local version derived a Push/Text/Both value and wrote BOTH halves, which
    // meant verifying could switch push ON for a row she was deliberately
    // receiving by text alone.
    // carryOnRows — this screen's entry point is a plain "Set up texts" that
    // promises no particular feature, and SmsSetupSheet's own copy covers
    // "your morning notes and task updates". So: add text to what she already
    // receives, resurrect nothing she switched off.
    const patch = buildPostVerifyPatch(prefs, { carryOnRows: true });
    if (Object.keys(patch).length > 0) {
      const error = await updatePreferences(patch);
      if (error) {
        reportError("[HowKinReachesYou] post-verify channel save failed", error, { tags: { area: "delivery-channel" } });
        showSaveFailedToast();
      }
    }
    // Unconditional, and it doubles as the revert on the failure path: refetching
    // replaces the optimistic values with server truth. Her number IS verified
    // either way — the Edge Function wrote that — so only the channel is at risk.
    refetchPreferences();
  };

  const handleCopyNumber = async () => {
    try {
      await navigator.clipboard.writeText(KIN_NUMBER_E164);
      trackFeature("sms_optout_copy_number");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — the number is on screen for manual copy */
    }
  };

  const cardStyle: CSSProperties = {
    border: "1.5px solid #C8C8F4",
    borderRadius: 16,
    padding: 18,
  };

  const showPushBlock = !textOnlyDevice && (effectiveChannel === "push" || effectiveChannel === "both");
  // PERSISTENT by design. Once texts are a real option for her they stay on
  // screen regardless of where the chooser sits, because this block is the only
  // place that explains a paused number — and a mom who replied STOP has, by
  // definition, no text rows on to keep the block visible.
  const showTextBlock = smsLive && isPro;

  return (
    <>
      <div className="bg-white" style={cardStyle}>
        <p className="font-body text-xs mb-4" style={{ color: "#54525C" }}>
          Pick what Kin sends, then how it arrives.
        </p>

        {/* ── WHAT KIN SENDS ─────────────────────────────────────────────── */}
        <SubheadLabel>What Kin sends</SubheadLabel>

        {FEATURES.map((f, idx) => (
          <div key={f}>
            {idx > 0 && <div className="h-px my-4" style={{ backgroundColor: "#E8E8F4" }} />}
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="font-body text-sm font-semibold" style={{ color: "#1C1A3E" }}>
                  {ROW_LABEL[f]}
                </p>
                <p className="font-body text-xs mt-0.5" style={{ color: "#54525C" }}>
                  {ROW_SUBTEXT[f]}
                </p>
              </div>
              <ToggleSwitch
                checked={rowOn(f)}
                disabled={prefsLoading}
                onChange={(next) => handleRowToggle(f, next)}
                ariaLabel={`Toggle ${ROW_LABEL[f]}`}
              />
            </div>

            {/* Morning send time. Visible whenever the row is on, on EVERY
                channel — notifications_morning_time is the single field the
                dispatcher reads for push and text alike (#431). */}
            {f === "morning" && rowOn("morning") && (
              <div className="mt-3 flex items-center gap-3">
                <label htmlFor="kin-morning-time" className="font-body text-xs" style={{ color: "#54525C" }}>
                  Send at:
                </label>
                <input
                  id="kin-morning-time"
                  type="time"
                  value={prefs.notifications_morning_time || "07:00"}
                  onChange={(e) => handleTimeChange(e.target.value || "07:00")}
                  className="font-body"
                  style={timeInputStyle}
                />
              </div>
            )}

            {f === "weekly" && rowOn("weekly") && (
              <>
                <div className="mt-3">
                  <p className="font-body text-xs mb-2" style={{ color: "#54525C" }}>
                    Send on:
                  </p>
                  <div className="flex items-center gap-1.5">
                    {DAY_INITIALS.map((label, dayIdx) => {
                      const selected = (prefs.notifications_weekly_day_of_week ?? 0) === dayIdx;
                      return (
                        <button
                          key={dayIdx}
                          type="button"
                          onClick={() => handleWeeklyDayChange(dayIdx)}
                          aria-label={`Send on ${DAY_NAMES[dayIdx]}`}
                          aria-pressed={selected}
                          className="font-body"
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: 999,
                            fontSize: 12,
                            fontWeight: 600,
                            background: selected ? "#5050C8" : "#F0F0FF",
                            color: selected ? "#FFFFFF" : "#54525C",
                            border: selected ? "1.5px solid #5050C8" : "1.5px solid #C8C8F4",
                            cursor: "pointer",
                            padding: 0,
                          }}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-3">
                  <label htmlFor="kin-weekly-time" className="font-body text-xs" style={{ color: "#54525C" }}>
                    Send at:
                  </label>
                  <input
                    id="kin-weekly-time"
                    type="time"
                    value={prefs.notifications_weekly_time || "18:00"}
                    onChange={(e) => handleWeeklyTimeChange(e.target.value || "18:00")}
                    className="font-body"
                    style={timeInputStyle}
                  />
                </div>
              </>
            )}
          </div>
        ))}

        {/* ── HOW THEY ARRIVE ────────────────────────────────────────────── */}
        <div className="h-px my-4" style={{ backgroundColor: "#E8E8F4" }} />
        <SubheadLabel>How they arrive</SubheadLabel>

        {textOnlyDevice ? (
          <p className="font-body text-xs" style={{ color: "#54525C", lineHeight: 1.5 }}>
            These arrive as texts here. For push notifications, open Kin on your phone.
          </p>
        ) : !smsLive ? (
          /* Texts aren't switched on for this build, so there is nothing to
             choose between. Rendering the pills here was the sharpest edge of
             this bug: they showed UNLOCKED for a Pro account (the lock is keyed
             on tier, not on SMS being live), tapping Text wrote push=false
             everywhere, and the text setup block that would have explained it is
             itself smsLive-gated — so she got silence and an empty section. */
          <p className="font-body text-xs" style={{ color: "#54525C", lineHeight: 1.5 }}>
            These arrive as notifications on your phone.
          </p>
        ) : (
          <>
            <div style={{ display: "flex", gap: 6 }}>
              {(["push", "text", "both"] as Channel[]).map((key) => {
                const selected = current === key;
                const locked = !isPro && key !== "push";
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => void handleChannelSelect(key)}
                    aria-pressed={selected}
                    disabled={!anyRowOn && !locked}
                    aria-label={locked ? `${CHANNEL_LABEL[key]} — unlock with Pro` : CHANNEL_LABEL[key]}
                    className="font-body"
                    style={{
                      flex: 1,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 4,
                      padding: "8px 0",
                      borderRadius: 10,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: !anyRowOn && !locked ? "not-allowed" : "pointer",
                      opacity: !anyRowOn && !locked ? 0.5 : 1,
                      background: selected ? "#5050C8" : "#F0F0FF",
                      color: selected ? "#FFFFFF" : "#54525C",
                      border: selected ? "1.5px solid #5050C8" : "1.5px solid #C8C8F4",
                    }}
                  >
                    {locked && <Lock size={11} />}
                    {CHANNEL_LABEL[key]}
                  </button>
                );
              })}
            </div>
            {!isPro && (
              <p className="font-body text-xs mt-2" style={{ color: "#54525C", lineHeight: 1.5 }}>
                Texts come with Pro.
              </p>
            )}
            {isPro && !anyRowOn && (
              <p className="font-body text-xs mt-2" style={{ color: "#54525C", lineHeight: 1.5 }}>
                Switch something on above and Kin will ask where to send it.
              </p>
            )}
          </>
        )}

        {/* ── SETUP: PUSH ────────────────────────────────────────────────── */}
        {showPushBlock && (
          <div className="mt-4">
            <div className="h-px mb-4" style={{ backgroundColor: "#E8E8F4" }} />
            {permission === "granted" ? (
              <p className="font-body text-xs" style={{ color: "#54525C" }}>
                Push is on for this phone ✓
              </p>
            ) : (
              <>
                <p className="font-body text-xs" style={{ color: "#54525C", lineHeight: 1.5 }}>
                  {permission === "denied"
                    ? "Notifications are switched off for Kin in your iPhone Settings, so nothing can land."
                    : "Kin needs your okay to send notifications to this phone."}
                </p>
                <button
                  type="button"
                  onClick={() => openIOSNotificationSettings()}
                  className="font-body text-xs mt-2 underline"
                  style={{ color: "#5050C8", background: "none", border: "none", padding: 0, cursor: "pointer" }}
                >
                  Open iOS Settings →
                </button>
              </>
            )}
          </div>
        )}

        {/* ── SETUP: TEXT (persistent) ───────────────────────────────────── */}
        {showTextBlock && (
          <div className="mt-4">
            <div className="h-px mb-4" style={{ backgroundColor: "#E8E8F4" }} />

            {optedOut ? (
              /* (c) She replied STOP. The app may reflect this and nothing more —
                 there is deliberately no button here that claims to undo it. */
              <div
                className="font-body text-xs"
                style={{
                  background: "#FFF7ED",
                  border: "1px solid #FED7AA",
                  color: "#9A3412",
                  borderRadius: 10,
                  padding: "12px 14px",
                  lineHeight: 1.6,
                }}
              >
                <p style={{ fontWeight: 600, marginBottom: 6 }}>Your texts are paused.</p>
                <p>
                  You replied STOP, so your phone company stopped them — and that's the one
                  thing I can't switch back on from in here. Text START to the number below
                  and I'll pick right back up.
                </p>
                <button
                  onClick={handleCopyNumber}
                  aria-label={`Copy Kin's number ${KIN_NUMBER_DISPLAY}`}
                  className="font-body font-semibold"
                  style={{
                    marginTop: 10,
                    padding: "6px 12px",
                    borderRadius: 999,
                    fontSize: 12.5,
                    color: "#5050C8",
                    background: "#FFFFFF",
                    border: "1px solid #C8C8F4",
                    cursor: "pointer",
                  }}
                >
                  {copied ? "✓ Copied!" : "📋 " + KIN_NUMBER_DISPLAY}
                </button>
              </div>
            ) : verified ? (
              /* (b) Verified and live. */
              <>
                <p className="font-body text-sm font-semibold" style={{ color: "#1C1A3E" }}>
                  Texting {formatUsNumber(prefs.phone_number!)} · Verified ✓
                </p>
                <div className="flex items-center gap-4 mt-3">
                  <button
                    onClick={() => setSheetOpen(true)}
                    className="font-body text-xs underline"
                    style={{ color: "#54525C", background: "none", border: "none", padding: 0, cursor: "pointer" }}
                  >
                    Change number
                  </button>
                  {/* Collapsed reminder, not the full card — she's already seen the
                      loud version on the SmsSetupSheet success screen. */}
                  {!contactHelperOpen && (
                    <button
                      onClick={() => setContactHelperOpen(true)}
                      className="font-body text-xs underline"
                      style={{ color: "#5050C8", background: "none", border: "none", padding: 0, cursor: "pointer" }}
                    >
                      Save Kin as a contact
                    </button>
                  )}
                </div>
                {contactHelperOpen && <SaveKinContactBlock variant="quiet" />}
              </>
            ) : (
              /* (a) No verified number yet. The ONE place the full save-as-contact
                 helper renders on this screen — it used to show here and again on
                 the verified card, which is the duplicate that came out. */
              <>
                <p className="font-body text-sm" style={{ color: "#54525C", lineHeight: 1.5 }}>
                  Give Kin your number and get a text instead of a ping on your lock screen.
                </p>
                <button
                  onClick={() => setSheetOpen(true)}
                  className="font-body font-semibold text-white"
                  style={{
                    marginTop: 14,
                    padding: "10px 18px",
                    borderRadius: 999,
                    fontSize: 13,
                    backgroundColor: "#5050C8",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  Set up texts
                </button>
                <SaveKinContactBlock variant="quiet" />
              </>
            )}

            {/* ── T4 (#1141): TEXT BACK & FORTH ─────────────────────────────
                Only once she has a verified number and has not replied STOP.
                Offering a conversation switch to someone who cannot receive a
                text is a control that does nothing, and offering it to someone
                who opted out would read as a way back in — which the block
                above is careful to say it is not. */}
            {verified && !optedOut && (
              <div className="mt-4">
                <div className="h-px mb-4" style={{ backgroundColor: "#E8E8F4" }} />
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p className="font-body text-sm font-semibold" style={{ color: "#1C1A3E" }}>
                      Text back &amp; forth with Kin
                    </p>
                    <p className="font-body text-xs mt-1" style={{ color: "#54525C", lineHeight: 1.5 }}>
                      Ask her things, add reminders, brain dump at midnight.
                    </p>
                  </div>
                  <ToggleSwitch
                    checked={twoWayOn}
                    onChange={handleTwoWayToggle}
                    ariaLabel="Toggle Text back and forth with Kin"
                  />
                </div>
                {!twoWayOn && (
                  <p className="font-body text-xs mt-2" style={{ color: "#54525C", lineHeight: 1.5 }}>
                    Your morning notes still arrive — this only turns off texting back.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── T4 (#1141): FREE-TIER TEASER ───────────────────────────────────
            Where a Pro user sees the toggle, a free account sees what it is.
            Shown only when SMS is live at all — teasing a feature that is
            switched off install-wide would be a promise nobody can keep. */}
        {smsLive && !isPro && (
          <div className="mt-4">
            <div className="h-px mb-4" style={{ backgroundColor: "#E8E8F4" }} />
            <div
              style={{
                background: "#F8F8FF",
                border: "1.5px solid #C8C8F4",
                borderRadius: 12,
                padding: "14px 16px",
              }}
            >
              <p className="font-body text-sm font-semibold" style={{ color: "#1C1A3E" }}>
                💬 Text with Kin
              </p>
              <p className="font-body text-xs mt-1" style={{ color: "#54525C", lineHeight: 1.5 }}>
                Get answers, add reminders, brain dump at midnight. Comes with Pro 💜
              </p>
              <div className="mt-2">
                <ProUpsellLink label="See what's included 💜" fontSize={12} />
              </div>
            </div>
          </div>
        )}
      </div>

      <NotificationSoftPrompt
        open={softPromptOpen}
        variant="settings"
        onAccept={async () => {
          setSoftPromptOpen(false);
          const action = pending;
          setPending(null);
          if (user) await markSoftPrompted(user.id);
          let granted = false;
          try {
            granted = await requestPushPermission();
          } catch {
            // ignore — we re-read the real status below
          }
          const postStatus = await getPushPermissionStatus();
          setPermission(postStatus);
          if (granted && action) {
            if (user) identifyOneSignalUser(user.id);
            if (action.kind === "row") await commitRowOn(action.feature, effectiveChannel);
            else await commitChannel(action.channel);
            return;
          }
          showDenialToast();
        }}
        onDecline={async () => {
          setSoftPromptOpen(false);
          setPending(null);
          if (user) await markSoftPrompted(user.id);
          // No toast — "Not right now" is a choice, not a denial.
        }}
      />

      <SmsSetupSheet isOpen={sheetOpen} onClose={() => setSheetOpen(false)} onVerified={handleVerified} />
    </>
  );
};

const CHANNEL_LABEL: Record<Channel, string> = {
  push: "Push",
  text: "Text",
  both: "Both",
};

const timeInputStyle: CSSProperties = {
  fontSize: 14,
  color: "#1C1A3E",
  border: "1.5px solid #C8C8F4",
  borderRadius: 10,
  padding: "6px 10px",
  background: "#FAFAFF",
};

const SubheadLabel = ({ children }: { children: React.ReactNode }) => (
  <p
    className="font-body text-xs font-semibold uppercase tracking-widest mb-3"
    style={{ color: "#9090A8" }}
  >
    {children}
  </p>
);

interface ToggleSwitchProps {
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
  ariaLabel: string;
}

const ToggleSwitch = ({ checked, disabled, onChange, ariaLabel }: ToggleSwitchProps) => (
  <button
    role="switch"
    aria-checked={checked}
    aria-label={ariaLabel}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    style={{
      position: "relative",
      width: 44,
      height: 26,
      borderRadius: 999,
      background: checked ? "#5050C8" : "#D8D8E8",
      transition: "background 0.2s ease",
      flexShrink: 0,
      opacity: disabled ? 0.5 : 1,
      border: "none",
      padding: 0,
      cursor: disabled ? "not-allowed" : "pointer",
    }}
  >
    <span
      style={{
        position: "absolute",
        top: 3,
        left: checked ? 21 : 3,
        width: 20,
        height: 20,
        borderRadius: "50%",
        background: "#FFFFFF",
        transition: "left 0.2s ease",
        boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
      }}
    />
  </button>
);

export default HowKinReachesYou;
